<template>
  <AttrBase v-slot="{ hover }" :label="label">
    <slot name="extra" />
    <div
      class="w-48 h-9 border border-transparent _container -mr-3 transition-all"
      :class="{
        ' !border-border-active': hover && !disabled,
        ' alphaHidden !w-32': alphaHidden
      }"
    >
      <!-- 色值输入框 -->
      <a-input
        ref="inputHex"
        v-model:value="hex"
        :bordered="false"
        spellcheck="false"
        :disabled="disabled"
        :maxlength="6"
        class="text-base font-medium w-full !p-0 !pl-4 !pr-1 bg-transparent h-full input-color"
        @change="onHexChange"
        @focus="focus"
        @blur="onBlur"
      />
      <!-- 透明度输入框 -->
      <div
        v-if="!alphaHidden"
        class="border border-transparent border-t-0 border-b-0 transition-all"
        :class="{
          ' !border-border-active': hover && !disabled
        }"
      >
        <a-input-number
          v-model:value="alpha"
          :min="0"
          :max="100"
          :bordered="false"
          :disabled="disabled"
          :controls="false"
          :formatter="(value:number) => `${round(value)}%`"
          class="text-base font-medium w-full"
          @change="onHexChange"
        />
      </div>
      <!-- 颜色选择器 -->
      <div
        class="flex items-center pl-2 h-full border border-r-0 border-t-0 border-b-0 border-transparent transition-all"
        :class="{
          ' !border-border-active': hover && !disabled && alphaHidden
        }"
      >
        <color-picker
          v-if="!disabled"
          v-model:pureColor="pureColor"
          round-history
          disable-alpha
          picker-type="chrome"
          format="hex8"
          theme="black"
          :debounce="0"
          @update:pure-color="onColorChange"
        />
        <div
          v-show="disabled"
          class="w-6 h-6 transition-all"
          :class="{
            'attr-disabled': disabled
          }"
          :style="{
            'background-color': pureColor
          }"
        ></div>
      </div>
    </div>
  </AttrBase>
</template>
<script lang="ts" setup>
import { round } from 'lodash-es';
import tinycolor from 'tinycolor2';
import { ref, watch } from 'vue';

const props = defineProps({
  label: {
    type: String,
    default: '颜色'
  },
  value: {
    type: String,
    default: undefined
  },
  alphaHidden: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const hex = ref<string>();
const alpha = ref<number>(100);
const pureColor = ref<string>();

const init = () => {
  const color = props.value;
  if (!color) return;
  const _color = tinycolor(color);
  const _hex = _color.toHex();
  const _alpha = _color.getAlpha() * 100;
  if (hex.value != _hex) hex.value = _hex;
  if (alpha.value != _alpha) alpha.value = _alpha;
  if (pureColor.value != color) pureColor.value = color;
};
watch(() => props.value, init, { immediate: true });

const emit = defineEmits<{
  (event: 'change', imagePath: string): void;
}>();

const onHexChange = () => {
  const _color = tinycolor(hex.value);
  if (!_color.isValid() || (hex.value?.length ?? 0) < 6) return;
  _color.setAlpha(alpha.value / 100);
  emit('change', _color.toHex8String());
};

const onColorChange = () => {
  if (pureColor.value) emit('change', pureColor.value);
};
const inputHex = ref<HTMLInputElement>();
function focus() {
  inputHex.value?.setSelectionRange(0, 6);
}

const onBlur = () => {
  const _color = tinycolor(hex.value);
  if (!_color.isValid() || (hex.value?.length ?? 0) < 6) {
    init();
  }
};
</script>

<style lang="less" scoped>
._container {
  display: grid;
  grid-template-columns: 1fr 65px 45px;
  &.alphaHidden {
    grid-template-columns: 1fr 45px;
  }
}
:deep(.vc-color-wrap) {
  width: 24px !important;
  height: 24px !important;
  margin: 0 !important;
  padding: 0 !important;
}
.input-color::selection {
  background: #2559c9;
}
</style>
